import type { GraphSim } from './sim.js';
/**
 * Aurora Atlas canvas renderer — nebula + stars + grid + nodes with halo + labels.
 * 디자인 renderer.js 를 TS 로 포팅. CSS variables 그대로 읽어 theme 반영.
 */
import type { GraphCategory, SimNode, Theme } from './types.js';

interface Star {
  x: number;
  y: number;
  r: number;
  tw: number;
  spd: number;
  depth: number;
}

interface Nebula {
  x: number;
  y: number;
  r: number;
  which: number;
}

export class GraphRenderer {
  canvas: HTMLCanvasElement;
  sim: GraphSim;
  ctx: CanvasRenderingContext2D;
  dpr: number;
  W = 0;
  H = 0;

  cam = { x: 0, y: 0, scale: 1.0, targetScale: 1.0 };

  stars: Star[] = [];
  nebs: Nebula[] = [];

  hoverId: string | null = null;
  focusId: string | null = null;
  activeCats: Set<string> | null = null;
  searchHits: Set<string> | null = null;
  linkMap = new Map<string, Set<string>>();
  categories: GraphCategory[];
  getTheme: () => Theme;

  private _ro?: ResizeObserver;

  constructor(
    canvas: HTMLCanvasElement,
    sim: GraphSim,
    categories: GraphCategory[],
    getTheme: () => Theme,
  ) {
    this.canvas = canvas;
    this.sim = sim;
    this.categories = categories;
    this.getTheme = getTheme;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    this.ctx = ctx;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);

    for (let i = 0; i < 360; i++) {
      this.stars.push({
        x: (Math.random() - 0.5) * 4000,
        y: (Math.random() - 0.5) * 4000,
        r: Math.random() * 1.4 + 0.2,
        tw: Math.random() * Math.PI * 2,
        spd: 0.3 + Math.random() * 0.8,
        depth: 0.2 + Math.random() * 0.8,
      });
    }
    for (let i = 0; i < 6; i++) {
      this.nebs.push({
        x: (Math.random() - 0.5) * 1800,
        y: (Math.random() - 0.5) * 1400,
        r: 500 + Math.random() * 600,
        which: i % 3,
      });
    }

    for (const e of sim.edges) {
      let a = this.linkMap.get(e.source.id);
      if (!a) {
        a = new Set();
        this.linkMap.set(e.source.id, a);
      }
      a.add(e.target.id);
      let b = this.linkMap.get(e.target.id);
      if (!b) {
        b = new Set();
        this.linkMap.set(e.target.id, b);
      }
      b.add(e.source.id);
    }

    this.resize();
    window.addEventListener('resize', this.handleResize);
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.resize());
      this._ro.observe(canvas);
      if (canvas.parentElement) this._ro.observe(canvas.parentElement);
    }
  }

  private handleResize = () => this.resize();

  destroy(): void {
    window.removeEventListener('resize', this.handleResize);
    this._ro?.disconnect();
  }

  resize(): void {
    const c = this.canvas;
    const w = c.clientWidth || c.parentElement?.clientWidth || window.innerWidth;
    const h = c.clientHeight || c.parentElement?.clientHeight || window.innerHeight;
    c.width = Math.floor(w * this.dpr);
    c.height = Math.floor(h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.W = w;
    this.H = h;
  }

  ensureSize(): void {
    const c = this.canvas;
    const w = c.clientWidth || c.parentElement?.clientWidth || window.innerWidth;
    const h = c.clientHeight || c.parentElement?.clientHeight || window.innerHeight;
    if (c.width !== Math.floor(w * this.dpr) || c.height !== Math.floor(h * this.dpr)) {
      this.resize();
    }
  }

  worldToScreen(x: number, y: number) {
    return {
      x: (x - this.cam.x) * this.cam.scale + this.W / 2,
      y: (y - this.cam.y) * this.cam.scale + this.H / 2,
    };
  }

  screenToWorld(sx: number, sy: number) {
    return {
      x: (sx - this.W / 2) / this.cam.scale + this.cam.x,
      y: (sy - this.H / 2) / this.cam.scale + this.cam.y,
    };
  }

  zoomBy(factor: number, anchorSX: number, anchorSY: number): void {
    const before = this.screenToWorld(anchorSX, anchorSY);
    this.cam.scale = Math.max(0.18, Math.min(3.5, this.cam.scale * factor));
    const after = this.screenToWorld(anchorSX, anchorSY);
    this.cam.x += before.x - after.x;
    this.cam.y += before.y - after.y;
  }

  pan(dx: number, dy: number): void {
    this.cam.x -= dx / this.cam.scale;
    this.cam.y -= dy / this.cam.scale;
  }

  pickNode(sx: number, sy: number): SimNode | null {
    const w = this.screenToWorld(sx, sy);
    let best: SimNode | null = null;
    let bestD = Number.POSITIVE_INFINITY;
    for (const n of this.sim.nodes) {
      const dx = n.x - w.x;
      const dy = n.y - w.y;
      const r = (n.radius + 6) / this.cam.scale;
      const d2 = dx * dx + dy * dy;
      if (d2 < r * r && d2 < bestD) {
        best = n;
        bestD = d2;
      }
    }
    return best;
  }

  catColor(catId: string, alpha = 1): string {
    const cat = this.categories.find((c) => c.id === catId);
    const hue = cat ? cat.hue : 280;
    const theme = this.getTheme();
    const L = theme === 'dark' ? 0.72 : 0.55;
    const C = theme === 'dark' ? 0.16 : 0.14;
    return `oklch(${L} ${C} ${hue} / ${alpha})`;
  }

  private cssVar(name: string): string {
    return getComputedStyle(document.body).getPropertyValue(name).trim();
  }

  drawBackground(): void {
    const ctx = this.ctx;
    ctx.fillStyle = this.cssVar('--bg') || '#000';
    ctx.fillRect(0, 0, this.W, this.H);

    const grid = this.cssVar('--grid');
    if (grid) {
      ctx.save();
      ctx.strokeStyle = grid;
      ctx.lineWidth = 1;
      const gs = 80 * this.cam.scale;
      const ox = (((-this.cam.x * this.cam.scale + this.W / 2) % gs) + gs) % gs;
      const oy = (((-this.cam.y * this.cam.scale + this.H / 2) % gs) + gs) % gs;
      ctx.beginPath();
      for (let x = ox; x < this.W; x += gs) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, this.H);
      }
      for (let y = oy; y < this.H; y += gs) {
        ctx.moveTo(0, y);
        ctx.lineTo(this.W, y);
      }
      ctx.stroke();
      ctx.restore();
    }

    const nebColors = [
      this.cssVar('--neb-a') || 'rgba(120,80,200,0.18)',
      this.cssVar('--neb-b') || 'rgba(80,160,200,0.14)',
      this.cssVar('--neb-c') || 'rgba(200,90,180,0.12)',
    ];
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const n of this.nebs) {
      const s = this.worldToScreen(n.x, n.y);
      const r = n.r * this.cam.scale;
      const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, r);
      grad.addColorStop(0, nebColors[n.which] ?? 'transparent');
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    const t = performance.now() / 1000;
    const starColor = this.cssVar('--star') || 'rgba(255,255,255,0.7)';
    ctx.fillStyle = starColor;
    for (const s of this.stars) {
      const sx = (s.x - this.cam.x * s.depth) * this.cam.scale * 0.3 + this.W / 2;
      const sy = (s.y - this.cam.y * s.depth) * this.cam.scale * 0.3 + this.H / 2;
      if (sx < -10 || sx > this.W + 10 || sy < -10 || sy > this.H + 10) continue;
      const tw = 0.55 + 0.45 * Math.sin(t * s.spd + s.tw);
      ctx.globalAlpha = tw * s.depth;
      ctx.beginPath();
      ctx.arc(sx, sy, s.r * (0.6 + 0.4 * tw), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  draw(): void {
    this.ensureSize();
    this.cam.scale += (this.cam.targetScale - this.cam.scale) * 0.18;

    this.drawBackground();
    const ctx = this.ctx;
    const edgeColor = this.cssVar('--edge') || 'rgba(255,255,255,0.07)';
    const edgeActive = this.cssVar('--edge-active') || 'rgba(255,255,255,0.45)';

    const focusId = this.focusId;
    const focusSet = focusId ? this.linkMap.get(focusId) : null;
    const isVisible = (n: SimNode) => {
      if (this.activeCats && !this.activeCats.has(n.category)) return false;
      return true;
    };
    const isHit = (n: SimNode) => !this.searchHits || this.searchHits.has(n.id);
    const isFocusRel = (n: SimNode) =>
      !focusId || n.id === focusId || (focusSet ? focusSet.has(n.id) : false);

    // edges
    ctx.lineCap = 'round';
    for (const e of this.sim.edges) {
      const a = e.source;
      const b = e.target;
      if (!isVisible(a) || !isVisible(b)) continue;
      const A = this.worldToScreen(a.x, a.y);
      const B = this.worldToScreen(b.x, b.y);

      let active = false;
      let alpha = 1;
      if (focusId) {
        const inFocus = a.id === focusId || b.id === focusId;
        active = inFocus;
        alpha = inFocus ? 1 : 0.08;
      } else if (this.searchHits) {
        const hit = this.searchHits.has(a.id) || this.searchHits.has(b.id);
        active = hit;
        alpha = hit ? 1 : 0.1;
      } else if (this.hoverId) {
        const inHover = a.id === this.hoverId || b.id === this.hoverId;
        active = inHover;
        alpha = inHover ? 1 : 0.5;
      }

      ctx.globalAlpha = alpha;
      ctx.strokeStyle = active ? edgeActive : edgeColor;
      ctx.lineWidth = active ? 1.0 : 0.6;
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(B.x, B.y);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // nodes
    const haloMul = Number.parseFloat(this.cssVar('--halo')) || 1;
    const nodeStroke = this.cssVar('--node-stroke') || 'rgba(255,255,255,0.14)';
    const fg = this.cssVar('--fg') || '#e8ecf5';
    const bg = this.cssVar('--bg') || '#05060a';

    for (const n of this.sim.nodes) {
      if (!isVisible(n)) continue;
      const visibleHit = isHit(n) && isFocusRel(n);
      let alpha = 1;
      if (focusId) alpha = visibleHit ? 1 : 0.12;
      else if (this.searchHits) alpha = this.searchHits.has(n.id) ? 1 : 0.18;
      else if (this.hoverId && this.hoverId !== n.id) {
        const hovSet = this.linkMap.get(this.hoverId);
        alpha = hovSet?.has(n.id) ? 1 : 0.55;
      }

      const p = this.worldToScreen(n.x, n.y);
      if (p.x < -50 || p.x > this.W + 50 || p.y < -50 || p.y > this.H + 50) continue;
      const r = n.radius * Math.max(0.8, this.cam.scale);

      const isActive = n.id === this.hoverId || n.id === focusId || this.searchHits?.has(n.id);
      if (isActive || haloMul > 0.7) {
        const haloR = r * (isActive ? 4.5 : 2.6);
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, haloR);
        grad.addColorStop(0, this.catColor(n.category, (isActive ? 0.55 : 0.22) * alpha * haloMul));
        grad.addColorStop(1, this.catColor(n.category, 0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, haloR, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.fillStyle = this.catColor(n.category, alpha);
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = nodeStroke;
      ctx.lineWidth = 1;
      ctx.globalAlpha = alpha * 0.9;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r + 0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;

      const showLabel = this.cam.scale > 1.0 || isActive || (n.degree > 8 && this.cam.scale > 0.55);
      if (showLabel) {
        const labelAlpha = Math.min(1, (this.cam.scale - 0.55) * 1.6) * alpha;
        if (labelAlpha > 0.05) {
          ctx.globalAlpha = labelAlpha;
          ctx.font = `${isActive ? '600' : '500'} ${isActive ? 13 : 11}px var(--font-sans), sans-serif`;
          ctx.fillStyle = fg;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.shadowColor = bg;
          ctx.shadowBlur = 6;
          ctx.fillText(n.title, p.x, p.y + r + 5);
          ctx.shadowBlur = 0;
          ctx.globalAlpha = 1;
        }
      }
    }
  }

  drawMinimap(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.clientWidth * this.dpr;
    canvas.height = canvas.clientHeight * this.dpr;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    ctx.fillStyle = this.cssVar('--bg-2') || '#11141f';
    ctx.fillRect(0, 0, w, h);

    if (this.sim.nodes.length === 0) return;
    const xs = this.sim.nodes.map((n) => n.x);
    const ys = this.sim.nodes.map((n) => n.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = 30;
    const sw = (w - pad * 2) / Math.max(1, maxX - minX);
    const sh = (h - pad * 2) / Math.max(1, maxY - minY);
    const s = Math.min(sw, sh);
    const ox = pad - minX * s + (w - pad * 2 - (maxX - minX) * s) / 2;
    const oy = pad - minY * s + (h - pad * 2 - (maxY - minY) * s) / 2;

    for (const n of this.sim.nodes) {
      ctx.fillStyle = this.catColor(n.category, 0.9);
      ctx.beginPath();
      ctx.arc(n.x * s + ox, n.y * s + oy, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    const tl = {
      x: this.cam.x - this.W / 2 / this.cam.scale,
      y: this.cam.y - this.H / 2 / this.cam.scale,
    };
    const br = {
      x: this.cam.x + this.W / 2 / this.cam.scale,
      y: this.cam.y + this.H / 2 / this.cam.scale,
    };
    const r = {
      x: tl.x * s + ox,
      y: tl.y * s + oy,
      w: (br.x - tl.x) * s,
      h: (br.y - tl.y) * s,
    };
    ctx.strokeStyle = this.cssVar('--fg-muted') || '#8b93a7';
    ctx.lineWidth = 1;
    ctx.strokeRect(r.x, r.y, r.w, r.h);
    ctx.fillStyle = `${this.cssVar('--fg') || '#e8ecf5'}15`;
    ctx.fillRect(r.x, r.y, r.w, r.h);
  }
}
